<template>
  <div class="ma-0 pa-0 mt-n10">
    <v-container>
      <div>
        <CRUDList class="mt-10" :canAdd="false"
          :canEdit="false" :canSave="true" :showSelect="false"
          :enableFixedHeaders="true" :hideDefaultFooter="true" :displayTopPagination="false"
          :headers="headers" :baseCrudKey="baseCrudKey"
          tableWrapperDynamicClassname='taxroll-table-wrapper'
          tableDynamicClassname='taxroll-custom-table' tableDynamicHeight='300'
          :loadingData="gridLoading.flag" :loadingText="gridLoading.text"
          :defaultSortBy="defaultSortBy" @loadCRUDList="getAccessorFileMergeAndFinalizeMethod">
        </CRUDList>
        <v-row>
          <p class="input-text-size mt-4">{{ statusMessage }}</p>
          <v-col cols="2"></v-col>
          <v-col>
            <!-- Every merge only inserts, so a second one started before the first has finished
                 duplicates what it merged. The buttons disable themselves once the grid shows the
                 merged records, but that is only true after the merge has returned AND the grid
                 has reloaded - mergeInProgress covers the window in between. -->
            <v-btn color="primary" small dense class="mr-2 mt-5"
              @click="mergeTaxOwner" :loading="mergeInProgress === 'taxOwner'"
              :disabled="isMergeDisabled('taxOrOwnerCount')"
              >Merge Tax/Owner</v-btn>
            <v-btn color="primary" small dense class="mr-2 mt-5"
              @click="mergeSummary" :loading="mergeInProgress === 'summary'"
              :disabled="isMergeDisabled('summaryCount')"
              >Merge Summary</v-btn>
            <v-btn color="primary" small dense class="mt-5"
              @click="mergeComments" :loading="mergeInProgress === 'comments'"
              :disabled="isMergeDisabled('commentsCount')"
              >Merge Comments</v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="text-center">
            <v-btn color="primary" small dense class="mt-5"
              @click="changeDefaultTaxYear">
              Change Default Tax Year
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-container>
  </div>
</template>

<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import CRUDList from '../../../common/base/crud-2.0/CRUDList.vue';
import { mergeNFinalizeHeaders } from '../../../shared/Utils/taxHeaders.utils';
import { defaultGridLoading, manageGridLoading } from '../../../../util/shared/vue-global';
import { getTaxYearStatus } from '../../common-utils/tax-common.util';
import { taxConfigScope } from '../../../../util/shared/system-config-scopes';
import { toShortDate } from '../../../../util/shared/tmc-global';
import '../../../../assets/style.css';

// Stable row identifiers sent by the backend (accessorFileMergeAndFinalize.util.js). The rows used
// to be found by their display label, so rewording a label broke the merge buttons' enable logic -
// match on these instead and the labels are free text.
const mergeAndFinalizeRowKeys = {
  expected: 'expected',
  total: 'total',
};

/* The three "roll to next year" steps on Import & Balance, in the words the warning below uses.
   Murray's 2026 conversion was merged without Roll Mortgage Codes having been run and went live
   with ~300 records missing their mortgage codes; by the time a merge is clicked that is no
   longer fixable by rolling, so this is the last point at which saying so is any use. Keyed on
   the backend's roll keys (assessorRollStatus.util.js). */
const ROLL_LABELS = {
  comments: 'Comments',
  mtgCodes: 'Mortgage codes',
  statusCodes: 'Status codes',
};

export default {
  name: 'MergeAndFinalize',
  data() {
    return {
      baseCrudKey: 'mergeAndFinalize',
      headers: mergeNFinalizeHeaders,
      gridLoading: defaultGridLoading(),
      statusPolling: null,
      statusMessage: '',
      // null | 'taxOwner' | 'summary' | 'comments' - which merge is running right now. Held from
      // the click until the grid has been reloaded, so the counts the buttons key off are the
      // post-merge ones before anything becomes clickable again.
      mergeInProgress: null,
    };
  },
  components: {
    CRUDList,
  },
  created() {
    this.loadConfig({ requiredScope: 'taxConfig' });
  },
  computed: {
    ...mapGetters('base/crud', [
      'items',
    ]),
    ...mapState({
      taxSettingItem: (state) => state.SystemConfig.taxSettingItem,
      mergeAndFinalizeItems: (state) => state.base.crud.items.mergeAndFinalize,
    }),
    getTotalAndExpetedTotalsItem() {
      const getItemArray = [mergeAndFinalizeRowKeys.total, mergeAndFinalizeRowKeys.expected];
      const getTotalAndExpetedTotals = (this.mergeAndFinalizeItems || [])
        .filter((ft) => getItemArray.includes(ft.rowKey));
      return getTotalAndExpetedTotals || [];
    },
    defaultSortBy() {
      return [
        {
          column: 'taxYear',
          direction: 'desc',
        },
      ];
    },
  },
  methods: {
    manageGridLoading,
    ...mapMutations('base/crud', ['setItem', 'setItems']),
    ...mapActions(['reAuth', 'flashError', 'flashWarn', 'flashSuccess']),
    ...mapActions('SystemConfig', [
      'loadConfig', 'upsertConfigSpecificFields',
    ]),
    ...mapActions('tax/newTaxroll', [
      'loadAssessorRollStatus',
      'getAccessorFileMergeAndFinalize',
      'mergeAssessorTaxRollRecordsToTaxInquiryCollection',
      'pollingToGetAssessorTaxRollRecordsToTaxInquiryCollectionProgress',
      'mergeAssessorSummaryToTaxrollSummary',
      'mergeAssessorCommentsToTaxComments',
      'loadAssesorTaxTotal',
      'sendNewTaxRollConversionSummary',
    ]),
    ...mapActions('tax/taxInquires', [
      'callToUpdateTaxGlobalVariable',
    ]),
    async getAccessorFileMergeAndFinalizeMethod() {
      manageGridLoading(this, true, 'Searching...');
      await this.getAccessorFileMergeAndFinalize({
        baseCrudKey: this.baseCrudKey,
      });
      manageGridLoading(this, false);
    },
    /* The backend words its own refusals ("Tax year 2026 already has 86 summary records...") and
       they say more than any message this screen could invent, so they are shown as-is. */
    flashMergeProblem(result, fallback) {
      this.flashError((result && result.message) || fallback);
    },
    /* True to go ahead. Asked once per merge click and read fresh each time, because the rolls
       are run on the other tab and may well have been run since this screen was opened - a
       warning about a roll that has since been run would teach the operator to click through it.
       A status that could not be read warns about nothing: the merge is what the operator asked
       for, and an unreadable status is not evidence that a roll was skipped. */
    async confirmRollsHaveRun() {
      const status = await this.loadAssessorRollStatus();
      // null is "could not be read", which is not the same answer as "nothing has been rolled"
      if (!status) return true;
      const notRun = Object.keys(ROLL_LABELS).filter((key) => !status[key]);
      if (!notRun.length) return true;
      // "Mortgage codes" / "Comments and Mortgage codes" / "Comments, Mortgage codes and Status codes"
      const which = notRun.map((key) => ROLL_LABELS[key]);
      const listed = which.length > 1
        ? `${which.slice(0, -1).join(', ')} and ${which[which.length - 1]}`
        : which[0];
      return window.confirm(`${listed} have not been rolled for this file.\n\n`
        + 'Rolling has to be done on Import & Balance before the merge - afterwards the records '
        + 'are live and would have to be corrected by hand.\n\nMerge anyway?');
    },
    async mergeTaxOwner() {
      if (this.mergeInProgress) return;
      if (!await this.confirmRollsHaveRun()) return;
      this.mergeInProgress = 'taxOwner';
      let result;
      try {
        result = await this.mergeAssessorTaxRollRecordsToTaxInquiryCollection();
      } catch (e) {
        this.mergeInProgress = null;
        throw e;
      }
      if (result.statusCode === 204) {
        // The merge runs on past the response; the polling below clears mergeInProgress once it
        // has finished and the grid has been reloaded.
        this.startTheCounter();
      } else {
        this.mergeInProgress = null;
        this.flashMergeProblem(result, 'There was a problem while merging tax/owner.');
      }
    },
    startTheCounter() {
      this.statusPolling = setInterval(() => {
        this.callThePollingFunc();
      }, 2200);
    },
    callThePollingFunc() {
      const loadPromise = this.pollingToGetAssessorTaxRollRecordsToTaxInquiryCollectionProgress();
      loadPromise.then(async (res) => {
        if (res.statusCode === 202) {
          this.statusMessage = `${res.totalRecords} records are being merged to tax inquiry collection.`;
          return;
        }
        clearInterval(this.statusPolling);
        this.statusMessage = '';
        if (res.statusCode === 200) {
          this.flashSuccess('Tax/Owner merged successfully.');
          await this.getAccessorFileMergeAndFinalizeMethod();
        } else {
          this.flashError('There was a problem while merging tax/owner.');
        }
        // Last: the button must not come back before the grid shows what the merge did.
        this.mergeInProgress = null;
      });
    },
    /* Both of these are insert-only and both used to leave their button live until the grid
       happened to catch up, which is how a 2026 UAT roll got two copies of every district
       summary. The merge now holds the buttons itself and the backend refuses a second run. */
    async mergeSummary() {
      await this.runMerge('summary', this.mergeAssessorSummaryToTaxrollSummary,
        'Summary merged successfully.', 'There was a problem while merging summary.');
    },
    async mergeComments() {
      await this.runMerge('comments', this.mergeAssessorCommentsToTaxComments,
        'Comments merged successfully.', 'There was a problem while merging comments.');
    },
    async runMerge(which, mergeAction, successText, failureText) {
      if (this.mergeInProgress) return;
      if (!await this.confirmRollsHaveRun()) return;
      this.mergeInProgress = which;
      try {
        const result = await mergeAction();
        if (result.statusCode === 200) {
          this.flashSuccess(successText);
          // Awaited, so the counts - and with them the buttons' own disabled state - are the
          // post-merge ones by the time this one is released.
          await this.getAccessorFileMergeAndFinalizeMethod();
        } else {
          this.flashMergeProblem(result, failureText);
        }
      } finally {
        this.mergeInProgress = null;
      }
    },
    async changeDefaultTaxYear() {
      const { currentTax } = getTaxYearStatus();
      const assessorTaxTotal = await this.loadAssesorTaxTotal({});
      const totalTaxAmount = assessorTaxTotal.totalTaxAmount || 0;

      if (currentTax) {
        const newTaxRollDate = toShortDate();
        const payload = {
          calledScope: taxConfigScope.scope,
          item: taxConfigScope.buildSpecificItem(this.taxSettingItem.id, {
            [taxConfigScope.specificFields.taxYear]: currentTax,
            [taxConfigScope.specificFields.newTaxRollDate]: newTaxRollDate,
            [taxConfigScope.specificFields.newTaxRollAmt]: totalTaxAmount,
          }),
        };
        await this.upsertConfigSpecificFields(payload);
        this.callToUpdateTaxGlobalVariable();
        this.flashSuccess('Default tax year changed successfully.');
        // The year change is done and is not undone by anything below: the summary email is a
        // separate step reported with its own message, so a mail-server outage or a county with
        // no Tax Website recipients never reads as a failed conversion. The counts and the total
        // are calculated on the server from the new year's records, not sent from here.
        await this.emailConversionSummary(currentTax, newTaxRollDate);
      }
    },
    /* The flash is a snackbar - one message at a time - so whichever message lands last has to
       carry both facts. Saying only "Conversion summary emailed to ..." would replace the
       confirmation that the year changed at all. */
    async emailConversionSummary(taxYear, newTaxRollDate) {
      const result = await this.sendNewTaxRollConversionSummary({ taxYear, newTaxRollDate });
      if (!result.sent) {
        this.flashWarn(`Default tax year changed. ${result.reason || 'No summary email was sent.'}`);
        return;
      }
      // A non-production environment redirects the mail (mail-gate.js), so the county addresses
      // are named as the intended recipients rather than as where it actually went.
      const redirected = result.mode && result.mode !== 'send';
      const sentTo = (result.recipients || []).join(', ');
      this.flashSuccess(`Default tax year changed. Conversion summary ${redirected ? 'addressed' : 'emailed'} to ${sentTo}.`);
    },
    /* Any merge in flight disables all three, not just its own button: they share one lock on the
       server, and Merge Comments reads the tax records Merge Tax/Owner is still writing. */
    isMergeDisabled(fromWhere) {
      if (this.mergeInProgress) return true;
      return this.checkIfMergeTaxOwnerIsDisabled(fromWhere);
    },
    checkIfMergeTaxOwnerIsDisabled(fromWhere) {
      const getTotalAndExpetedTotalsItemList = this.getTotalAndExpetedTotalsItem;
      if (!getTotalAndExpetedTotalsItemList.length) return false;
      const getExpectedRecord = getTotalAndExpetedTotalsItemList.find((ft) => ft.rowKey === mergeAndFinalizeRowKeys.expected);
      const getTotalRecord = getTotalAndExpetedTotalsItemList.find((ft) => ft.rowKey === mergeAndFinalizeRowKeys.total);
      if (!getExpectedRecord || !getTotalRecord) return false;
      // if the expected record is less than the total record, then disable the button
      if ((getExpectedRecord[fromWhere] || 0) <= (getTotalRecord[fromWhere] || 0)) {
        return true;
      }
      return false;
    },
  },
};
</script>
